import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { logApiError } from '@/lib/errors';
import { useToast } from '@/hooks/use-toast';

const SUPPORTED_PHOTO_MIME_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
    'image/avif',
    'video/mp4',
    'video/webm',
]);

export function useClaimPhotos(claimId: number, canWrite = true, enabled = true) {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const { data: response, isLoading } = useQuery({
        queryKey: ['claim-photos', claimId],
        queryFn: async () => {
            return await apiClient.claimPhotos.getClaimPhotos({ claimId });
        },
        enabled: !!claimId && enabled,
    });

    const uploadPhotoMutation = useMutation({
        mutationFn: async ({ file, caption }: { file: File; caption?: string }) => {
            return new Promise<import('@/api/generated/client/models').ClaimPhoto>((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = async () => {
                    try {
                        const base64String = (reader.result as string).split(',')[1];
                        const result = await apiClient.claimPhotos.createClaimPhoto({
                            claimId,
                            createClaimPhotoRequest: {
                                filename: file.name,
                                mimeType: file.type,
                                sizeBytes: file.size,
                                fileContent: base64String,
                                ...(caption != null ? { caption } : {}),
                            },
                        });
                        resolve(result);
                    } catch (error) {
                        reject(error);
                    }
                };
                reader.onerror = (error) => reject(error);
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['claim-photos', claimId] });
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const photos = useMemo(() => response?.items || [], [response]);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);
    const dragDepthRef = useRef(0);

    const uploadFiles = useCallback(async (files: File[]) => {
        const unsupportedFiles = files.filter((file) => !SUPPORTED_PHOTO_MIME_TYPES.has(file.type));
        const supportedFiles = files.filter((file) => SUPPORTED_PHOTO_MIME_TYPES.has(file.type));

        if (unsupportedFiles.length > 0) {
            toast({
                title: 'Unsupported file type',
                description: `These files will not upload because their file type is unsupported: ${unsupportedFiles.map((file) => file.name).join(', ')}`,
                variant: 'destructive',
            });
        }

        const failed: string[] = [];
        for (const file of supportedFiles) {
            try {
                await uploadPhotoMutation.mutateAsync({ file });
            } catch {
                failed.push(file.name);
            }
        }
        if (failed.length > 0) {
            toast({
                title: failed.length === files.length ? 'Upload failed' : 'Some uploads failed',
                description: `Could not upload: ${failed.join(', ')}`,
                variant: 'destructive',
            });
        }
    }, [toast, uploadPhotoMutation]);

    const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        if (!canWrite) return;
        const files = Array.from(event.target.files ?? []);
        if (files.length > 0) void uploadFiles(files);
        event.target.value = '';
    }, [canWrite, uploadFiles]);

    const handleDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (!canWrite || uploadPhotoMutation.isPending || !event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        dragDepthRef.current += 1;
        setIsDraggingFiles(true);
    }, [canWrite, uploadPhotoMutation.isPending]);

    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (!canWrite || uploadPhotoMutation.isPending || !event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
    }, [canWrite, uploadPhotoMutation.isPending]);

    const handleDragLeave = useCallback(() => {
        if (!isDraggingFiles) return;
        dragDepthRef.current -= 1;
        if (dragDepthRef.current <= 0) {
            dragDepthRef.current = 0;
            setIsDraggingFiles(false);
        }
    }, [isDraggingFiles]);

    const handleDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (!canWrite || uploadPhotoMutation.isPending) return;
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current = 0;
        setIsDraggingFiles(false);
        const files = Array.from(event.dataTransfer.files);
        if (files.length > 0) void uploadFiles(files);
    }, [canWrite, uploadFiles, uploadPhotoMutation.isPending]);

    const getPhotoBlob = useCallback(async (photoId: number) => {
        try {
            return await apiClient.claimPhotos.getClaimPhotoContent({ claimId, id: photoId });
        } catch (error) {
            logApiError(error, 'Get photo blob');
            throw error;
        }
    }, [claimId]);

    const getPhotoThumbnailBlob = useCallback(async (photoId: number, signal?: AbortSignal) => {
        try {
            return await apiClient.claimPhotos.getClaimPhotoThumbnail({ claimId, id: photoId }, { signal });
        } catch (error) {
            if (signal?.aborted) throw error;
            logApiError(error, 'Get photo thumbnail');
            throw error;
        }
    }, [claimId]);

    const downloadPhoto = async (photoId: number, filename: string) => {
        try {
            const blob = await getPhotoBlob(photoId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            logApiError(error, 'Download photo');
            toast({
                title: 'Failed to download photo',
                description: 'Please try again.',
                variant: 'destructive',
            });
        }
    };

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [lightboxBlobUrl, setLightboxBlobUrl] = useState<string | null>(null);
    const [lightboxLoading, setLightboxLoading] = useState(false);
    const [lightboxError, setLightboxError] = useState<string | null>(null);
    const lightboxRequestRef = useRef(0);

    const clearLightboxUrl = useCallback(() => {
        setLightboxBlobUrl((previousUrl) => {
            if (previousUrl) URL.revokeObjectURL(previousUrl);
            return null;
        });
    }, []);

    const loadLightbox = useCallback(async (index: number) => {
        const photo = photos[index];
        if (!photo) return;
        const requestId = lightboxRequestRef.current + 1;
        lightboxRequestRef.current = requestId;

        setLightboxError(null);
        setLightboxLoading(true);
        clearLightboxUrl();

        try {
            const blob = await getPhotoBlob(photo.id);
            const url = URL.createObjectURL(blob);
            if (lightboxRequestRef.current !== requestId) {
                URL.revokeObjectURL(url);
                return;
            }
            setLightboxBlobUrl(url);
        } catch {
            if (lightboxRequestRef.current === requestId) {
                setLightboxError('Failed to load media.');
            }
        } finally {
            if (lightboxRequestRef.current === requestId) {
                setLightboxLoading(false);
            }
        }
    }, [clearLightboxUrl, getPhotoBlob, photos]);

    const openLightbox = useCallback((index: number) => {
        setActiveIndex(index);
        setLightboxOpen(true);
        void loadLightbox(index);
    }, [loadLightbox]);

    const handleNavigate = useCallback((index: number) => {
        setActiveIndex(index);
        void loadLightbox(index);
    }, [loadLightbox]);

    const handleLightboxOpenChange = useCallback((open: boolean) => {
        setLightboxOpen(open);
        if (!open) {
            lightboxRequestRef.current += 1;
            setLightboxError(null);
            setLightboxLoading(false);
            clearLightboxUrl();
        }
    }, [clearLightboxUrl]);

    useEffect(() => () => {
        lightboxRequestRef.current += 1;
        clearLightboxUrl();
    }, [clearLightboxUrl]);

    const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

    const updatePhotoMutation = useMutation({
        mutationFn: async ({ photoId, filename, caption }: { photoId: number; filename?: string; caption?: string | null }) => {
            return await apiClient.claimPhotos.updateClaimPhoto({
                claimId,
                id: photoId,
                updateClaimPhotoRequest: {
                    ...(filename != null ? { filename } : {}),
                    ...(caption !== undefined ? { caption } : {}),
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['claim-photos', claimId] });
        },
    });

    const deletePhotoMutation = useMutation({
        mutationFn: async (photoId: number) => {
            await apiClient.claimPhotos.deleteClaimPhoto({ claimId, id: photoId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['claim-photos', claimId] });
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const handleDeleteConfirm = useCallback(async () => {
        if (deleteTargetId == null) return;
        try {
            await deletePhotoMutation.mutateAsync(deleteTargetId);
        } catch {
            toast({
                title: 'Failed to delete photo',
                description: 'Please try again.',
                variant: 'destructive',
            });
        } finally {
            setDeleteTargetId(null);
        }
    }, [deletePhotoMutation, deleteTargetId, toast]);

    return {
        photos,
        isLoading,
        uploadPhoto: uploadPhotoMutation.mutate,
        uploadPhotoAsync: uploadPhotoMutation.mutateAsync,
        isUploading: uploadPhotoMutation.isPending,
        getPhotoBlob,
        getPhotoThumbnailBlob,
        downloadPhoto,
        updatePhoto: updatePhotoMutation.mutate,
        updatePhotoAsync: updatePhotoMutation.mutateAsync,
        isUpdating: updatePhotoMutation.isPending,
        deletePhoto: deletePhotoMutation.mutate,
        deletePhotoAsync: deletePhotoMutation.mutateAsync,
        isDeleting: deletePhotoMutation.isPending,
        fileInputRef,
        isDraggingFiles,
        handleFileChange,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        openLightbox,
        lightboxOpen,
        activeIndex,
        handleNavigate,
        handleLightboxOpenChange,
        lightboxBlobUrl,
        lightboxLoading,
        lightboxError,
        deleteTargetId,
        setDeleteTargetId,
        handleDeleteConfirm,
    };
}
